import {
  type ImageNode,
  MAX_DESIGN_MM,
  TEXT_SCALE_Y_MAX,
  type TextNode,
} from '@repo/api-contracts';
import { apply, invert, nodeMatrix, type PointMm } from '@repo/design-renderer';
import { TEXT_LIMITS } from './text-limits';

export type HandleKind =
  | 'move'
  | 'rotate'
  | 'corner-tl'
  | 'corner-tr'
  | 'corner-br'
  | 'corner-bl'
  | 'edge-t'
  | 'edge-r'
  | 'edge-b'
  | 'edge-l'
  | 'side-l'
  | 'side-r';
export type CornerKind = 'corner-tl' | 'corner-tr' | 'corner-br' | 'corner-bl';
export type EdgeKind = 'edge-t' | 'edge-r' | 'edge-b' | 'edge-l';

export const isCornerKind = (kind: HandleKind): kind is CornerKind => kind.startsWith('corner-');
export const isEdgeKind = (kind: HandleKind): kind is EdgeKind => kind.startsWith('edge-');
export type BoxNode = ImageNode | TextNode;

export interface HandlePoint {
  readonly kind: HandleKind;
  readonly xMm: number;
  readonly yMm: number;
}

export const ROTATE_HANDLE_OFFSET_PX = 24;
export const HANDLE_HIT_TOLERANCE_PX = 10;
/** 회전 손잡이(흰 동그라미)의 보이는 반지름(px). */
export const ROTATE_HANDLE_RADIUS_PX = 9;
export const MIN_NODE_MM = 1;
/**
 * 변 가운데 핸들을 보이는 최소 화면 길이(px). 그 축이 이보다 짧으면(작은 글자 등) 가운데 핸들이 상자를 덮어
 * 옮기기를 가로채므로 그 축의 가운데 핸들을 숨긴다. 가운데에서 핸들까지가 히트 허용 거리 이상이 되는 길이다.
 */
export const MIN_EDGE_HANDLE_SPAN_PX = 2 * HANDLE_HIT_TOLERANCE_PX;

/**
 * 화면 축척(CSS px/mm) — 숫자 하나면 두 축이 같고, 사진 위 편집처럼 축마다 다르면 { x, y } 로 준다.
 * 핸들 자리(회전 핸들 24px)·히트 허용 거리(10px)를 화면 px 로 재기 위해서만 쓴다.
 */
export type HandleScale = number | { readonly x: number; readonly y: number };

const axesOf = (scale: HandleScale): { x: number; y: number } =>
  typeof scale === 'number' ? { x: scale, y: scale } : { x: scale.x, y: scale.y };

const center = (node: BoxNode): PointMm => ({
  xMm: node.x + node.width / 2,
  yMm: node.y + node.height / 2,
});

/** 핸들 판정은 플립을 무시한다. */
const withoutFlip = (node: BoxNode): BoxNode =>
  node.type === 'image' ? { ...node, flipX: false, flipY: false } : node;

/** 노드 로컬 점(상자 좌표, mm) → 컴포넌트 mm. */
const localToComponent = (node: BoxNode, x: number, y: number): PointMm => {
  const p = apply(nodeMatrix(node), { x, y });
  return { xMm: p.x, yMm: p.y };
};

/** 컴포넌트 mm → 노드 로컬(상자 좌표, mm). 플립은 무시하고 회전·위치만 역적용한다(핸들 판정용). */
const componentToUnrotated = (node: BoxNode, point: PointMm): { x: number; y: number } => {
  const inverse = invert(nodeMatrix(withoutFlip(node)));
  return inverse === null ? { x: 0, y: 0 } : apply(inverse, { x: point.xMm, y: point.yMm });
};

/**
 * 이미지는 모서리 4개(자유, Shift 면 비율 유지) + 변 가운데 4개(한 축만) + 회전(위 가운데에서 화면 24px 위),
 * 텍스트는 모서리 4개(글자 크기, Shift 면 줄 폭·세로 늘림 따로) + 좌우 변(줄 폭) + 위·아래 가운데(세로 늘림, scaleY)
 * + 회전. 화면에서 짧은 축의 가운데 핸들은 숨긴다(모서리는 늘 보인다).
 * 회전·위치를 반영한 컴포넌트 mm 다.
 */
export const handlePoints = (node: BoxNode, scale: HandleScale): readonly HandlePoint[] => {
  const unflipped = withoutFlip(node);
  const at = (kind: HandleKind, x: number, y: number): HandlePoint => ({
    kind,
    ...localToComponent(unflipped, x, y),
  });
  // 노드 로컬 위쪽(회전 반영)이 화면에서 1mm 당 몇 px 인지 — 축척이 축마다 달라도 화면에서 24px 위에 둔다.
  const axes = axesOf(scale);
  const rad = (node.rotationDeg * Math.PI) / 180;
  const upPxPerMm = Math.max(0.0001, Math.hypot(Math.sin(rad) * axes.x, Math.cos(rad) * axes.y));
  const acrossPxPerMm = Math.hypot(Math.cos(rad) * axes.x, Math.sin(rad) * axes.y);
  const rotate = at('rotate', node.width / 2, -ROTATE_HANDLE_OFFSET_PX / upPxPerMm);
  const showVertical = node.height * upPxPerMm >= MIN_EDGE_HANDLE_SPAN_PX;
  const showHorizontal = node.width * acrossPxPerMm >= MIN_EDGE_HANDLE_SPAN_PX;
  const edges = [
    showVertical ? [at('edge-t', node.width / 2, 0)] : [],
    showHorizontal && node.type === 'image' ? [at('edge-r', node.width, node.height / 2)] : [],
    showVertical ? [at('edge-b', node.width / 2, node.height)] : [],
    showHorizontal && node.type === 'image' ? [at('edge-l', 0, node.height / 2)] : [],
  ].flat();
  const corners = [
    at('corner-tl', 0, 0),
    at('corner-tr', node.width, 0),
    at('corner-br', node.width, node.height),
    at('corner-bl', 0, node.height),
  ];
  if (node.type === 'text')
    return [
      ...corners,
      at('side-l', 0, node.height / 2),
      at('side-r', node.width, node.height / 2),
      ...edges,
      rotate,
    ];
  return [...corners, ...edges, rotate];
};

/** 화면 허용 거리 안의 가장 가까운 핸들, 없으면 상자 안은 move, 밖은 null. */
export const hitHandle = (node: BoxNode, point: PointMm, scale: HandleScale): HandleKind | null => {
  const axes = axesOf(scale);
  let nearest: HandleKind | null = null;
  let nearestDistance = HANDLE_HIT_TOLERANCE_PX;
  for (const handle of handlePoints(node, scale)) {
    const distance = Math.hypot(
      (handle.xMm - point.xMm) * axes.x,
      (handle.yMm - point.yMm) * axes.y,
    );
    if (distance <= nearestDistance) {
      nearest = handle.kind;
      nearestDistance = distance;
    }
  }
  if (nearest !== null) return nearest;
  const local = componentToUnrotated(node, point);
  return local.x >= 0 && local.x <= node.width && local.y >= 0 && local.y <= node.height
    ? 'move'
    : null;
};

export const dragMove = <T extends BoxNode>(original: T, start: PointMm, current: PointMm): T => ({
  ...original,
  x: original.x + (current.xMm - start.xMm),
  y: original.y + (current.yMm - start.yMm),
});

export const nudge = <T extends BoxNode>(node: T, dxMm: number, dyMm: number): T => ({
  ...node,
  x: node.x + dxMm,
  y: node.y + dyMm,
});

const isLeftCorner = (corner: CornerKind): boolean =>
  corner === 'corner-tl' || corner === 'corner-bl';
const isTopCorner = (corner: CornerKind): boolean =>
  corner === 'corner-tl' || corner === 'corner-tr';

/** 끄는 모서리에서 반대쪽 모서리를 향한 부호 — 반대쪽이 로컬 원점 쪽이면 +1(크기가 +축으로 자란다). */
const growSign = (corner: CornerKind): { x: number; y: number } => ({
  x: isLeftCorner(corner) ? -1 : 1,
  y: isTopCorner(corner) ? -1 : 1,
});

/** 원래 상자에서 반대쪽 모서리까지 잰 현재점의 로컬 거리(끄는 방향이 +). */
const spanFromOpposite = (
  original: BoxNode,
  corner: CornerKind,
  current: PointMm,
): { x: number; y: number } => {
  const local = componentToUnrotated(original, current);
  return {
    x: isLeftCorner(corner) ? original.width - local.x : local.x,
    y: isTopCorner(corner) ? original.height - local.y : local.y,
  };
};

/** Shift 비율 배율 — 현재점(반대쪽 모서리 기준)을 원래 대각선에 정사영한 길이 ÷ 대각선 길이. */
const diagonalFactor = (original: BoxNode, corner: CornerKind, current: PointMm): number => {
  const span = spanFromOpposite(original, corner, current);
  const lengthSq = original.width ** 2 + original.height ** 2;
  return lengthSq > 0 ? (span.x * original.width + span.y * original.height) / lengthSq : 1;
};

/**
 * 크기를 width × height 로 바꾸고 끄는 모서리의 반대쪽 모서리를 컴포넌트 mm 에서 제자리에 둔다(회전 반영, 플립 무시).
 */
const resizeFromOppositeCorner = <T extends BoxNode>(
  original: T,
  corner: CornerKind,
  width: number,
  height: number,
): T => {
  const sign = growSign(corner);
  const unflipped = withoutFlip(original);
  const anchor = localToComponent(
    unflipped,
    sign.x > 0 ? 0 : original.width,
    sign.y > 0 ? 0 : original.height,
  );
  // 새 상자의 중심 = 반대쪽 모서리 + 회전한 (±폭/2, ±높이/2).
  const rad = (original.rotationDeg * Math.PI) / 180;
  const dx = (sign.x * width) / 2;
  const dy = (sign.y * height) / 2;
  const cx = anchor.xMm + dx * Math.cos(rad) - dy * Math.sin(rad);
  const cy = anchor.yMm + dx * Math.sin(rad) + dy * Math.cos(rad);
  return { ...original, width, height, x: cx - width / 2, y: cy - height / 2 };
};

/**
 * 모서리를 끌면 반대쪽 모서리를 고정한 채 크기를 바꾼다(회전 반영). 기본은 비율 없이 가로·세로를 따로 —
 * 반대쪽 모서리에서 현재점까지 로컬 축 거리가 새 크기다. proportional(Shift)이면 비율을 지킨다:
 * 배율 = 현재점을 원래 대각선에 정사영한 길이 ÷ 대각선 길이.
 */
export const dragResize = <T extends BoxNode>(
  original: T,
  corner: CornerKind,
  current: PointMm,
  proportional: boolean,
): T => {
  if (!proportional) {
    const span = spanFromOpposite(original, corner, current);
    return resizeFromOppositeCorner(
      original,
      corner,
      Math.max(MIN_NODE_MM, span.x),
      Math.max(MIN_NODE_MM, span.y),
    );
  }
  const factor = Math.max(
    MIN_NODE_MM / Math.min(original.width, original.height),
    diagonalFactor(original, corner, current),
  );
  return resizeFromOppositeCorner(
    original,
    corner,
    original.width * factor,
    original.height * factor,
  );
};

/** 레이아웃 높이 × scaleY 가 크기 상한(MAX_DESIGN_MM)과 배율 상한(TEXT_SCALE_Y_MAX)을 넘지 않게 자른다. */
const clampScaleY = (scaleY: number, layoutHeight: number): number =>
  Math.min(TEXT_SCALE_Y_MAX, layoutHeight > 0 ? MAX_DESIGN_MM / layoutHeight : scaleY, scaleY);

/**
 * 텍스트 모서리 — 반대쪽 모서리를 고정한다(회전 반영). 기본은 비율: 글자 크기·줄 폭·상자를 같은 배율로 바꿔
 * 줄바꿈과 세로 늘림 모양을 그대로 둔다(글자 크기 범위·크기 상한에서 멈춘다). free(Shift)면 줄 폭(좌우 변처럼)과
 * 세로 늘림 scaleY(위·아래 핸들처럼)를 따로 바꾼다. 높이는 commit 때 textHeightMm 로 다시 계산된다.
 */
export const dragTextCorner = (
  original: TextNode,
  corner: CornerKind,
  current: PointMm,
  free: boolean,
): TextNode => {
  if (free) {
    const span = spanFromOpposite(original, corner, current);
    const baseScale = original.scaleY ?? 1;
    const layoutHeight = original.height / baseScale;
    const scaleY = clampScaleY(
      (baseScale * Math.max(MIN_NODE_MM, span.y)) / original.height,
      layoutHeight,
    );
    const width = Math.min(MAX_DESIGN_MM, Math.max(MIN_NODE_MM, span.x));
    return {
      ...resizeFromOppositeCorner(original, corner, width, layoutHeight * scaleY),
      scaleY,
    };
  }
  const font = original.fontSizeMm;
  const { min, max } = TEXT_LIMITS.fontSizeMm;
  // 배율 1 은 늘 허용한다(이미 범위 밖인 노드도 끄는 순간 튀지 않게).
  const lower = Math.min(
    1,
    Math.max(min / font, MIN_NODE_MM / original.width, MIN_NODE_MM / original.height),
  );
  const upper = Math.max(
    1,
    Math.min(max / font, MAX_DESIGN_MM / original.width, MAX_DESIGN_MM / original.height),
  );
  const factor = Math.min(upper, Math.max(lower, diagonalFactor(original, corner, current)));
  return {
    ...resizeFromOppositeCorner(
      original,
      corner,
      original.width * factor,
      original.height * factor,
    ),
    fontSizeMm: font * factor,
  };
};

/** 변 가운데 핸들 축의 크기를 next 로 바꾸고 반대쪽 변을 고정한다(노드 로컬 축, 회전 반영). */
const resizeAlongEdge = <T extends BoxNode>(original: T, edge: EdgeKind, next: number): T => {
  const horizontal = edge === 'edge-l' || edge === 'edge-r';
  const size = horizontal ? original.width : original.height;
  // 고정할 변이 제자리에 있도록 중심을 그 로컬 축(회전 반영) 방향으로 옮긴다(앞쪽 변 고정이면 +, 뒤쪽 변 고정이면 −).
  const shift = edge === 'edge-r' || edge === 'edge-b' ? next - size : size - next;
  const rad = (original.rotationDeg * Math.PI) / 180;
  const axis = horizontal
    ? { x: Math.cos(rad), y: Math.sin(rad) }
    : { x: -Math.sin(rad), y: Math.cos(rad) };
  const c = center(original);
  const width = horizontal ? next : original.width;
  const height = horizontal ? original.height : next;
  return {
    ...original,
    width,
    height,
    x: c.xMm + (shift / 2) * axis.x - width / 2,
    y: c.yMm + (shift / 2) * axis.y - height / 2,
  };
};

/** 현재점까지 끈 변 가운데 핸들 축의 새 크기(최소 크기 이상). */
const edgeSizeAt = (original: BoxNode, edge: EdgeKind, current: PointMm): number => {
  const local = componentToUnrotated(original, current);
  const horizontal = edge === 'edge-l' || edge === 'edge-r';
  const size = horizontal ? original.width : original.height;
  const coordinate = horizontal ? local.x : local.y;
  return Math.max(
    MIN_NODE_MM,
    edge === 'edge-r' || edge === 'edge-b' ? coordinate : size - coordinate,
  );
};

/**
 * 변 가운데 핸들을 끌어 그 축의 크기만 바꾼다(비율 없음). 노드 로컬 축(회전 반영)으로 재고 반대쪽 변을 고정한다.
 */
export const dragResizeEdge = <T extends BoxNode>(
  original: T,
  edge: EdgeKind,
  current: PointMm,
): T => resizeAlongEdge(original, edge, edgeSizeAt(original, edge, current));

/**
 * 텍스트 위·아래 가운데 핸들 — 반대쪽 변을 고정한 채 글자를 세로로 늘린다. 상자 높이 비율만큼 scaleY 를 곱하고
 * 계약 상한(TEXT_SCALE_Y_MAX)과 상자 높이 상한(MAX_DESIGN_MM)에서 멈춘다. 높이는 레이아웃 높이 × scaleY 라 commit 때 다시 계산해도 같다.
 */
export const dragTextStretch = (
  original: TextNode,
  edge: 'edge-t' | 'edge-b',
  current: PointMm,
): TextNode => {
  const baseScale = original.scaleY ?? 1;
  const layoutHeight = original.height / baseScale;
  const scaleY = clampScaleY(
    (baseScale * edgeSizeAt(original, edge, current)) / original.height,
    layoutHeight,
  );
  return { ...resizeAlongEdge(original, edge, layoutHeight * scaleY), scaleY };
};

const RESIZE_CURSORS = ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'] as const;
const HANDLE_AXIS_DEG: Partial<Record<HandleKind, number>> = {
  'edge-l': 0,
  'edge-r': 0,
  'side-l': 0,
  'side-r': 0,
  'corner-tl': 45,
  'corner-br': 45,
  'edge-t': 90,
  'edge-b': 90,
  'corner-tr': 135,
  'corner-bl': 135,
};

/** 크기 핸들의 CSS 커서 — 핸들 방향에 노드 회전을 더해 45° 단위로 고른다. 회전·이동 핸들은 undefined. */
export const handleCursor = (kind: HandleKind, rotationDeg: number): string | undefined => {
  const base = HANDLE_AXIS_DEG[kind];
  if (base === undefined) return undefined;
  const step = Math.round((((base + rotationDeg) % 180) + 180) / 45) % 4;
  return RESIZE_CURSORS[step];
};

/** (-180, 180] 로 접는다(정확히 180° 는 180 으로 둔다). */
export const normalizeDeg = (deg: number): number => {
  const wrapped = ((deg % 360) + 360) % 360;
  return wrapped > 180 ? wrapped - 360 : wrapped;
};

/** Shift 없이 끌 때 직각(0·90·180·270°)에 붙는 거리(도). */
export const RIGHT_ANGLE_SNAP_DEG = 4;

/** 각도가 정확히 직각(0·90·180·270°)인가 — 회전 중 수평·수직 안내선을 보일지 정한다. */
export const isRightAngle = (deg: number): boolean =>
  Math.abs(deg - Math.round(deg / 90) * 90) < 1e-9;

/**
 * 회전 핸들이 위 가운데이므로 각도 = atan2(현재 − 중심) + 90°. snap(Shift)이면 15° 단위,
 * 아니면 직각에서 RIGHT_ANGLE_SNAP_DEG 안일 때 그 직각에 붙인다.
 */
export const dragRotate = <T extends BoxNode>(original: T, current: PointMm, snap: boolean): T => {
  const c = center(original);
  const raw = (Math.atan2(current.yMm - c.yMm, current.xMm - c.xMm) * 180) / Math.PI + 90;
  const right = Math.round(raw / 90) * 90;
  const deg = snap
    ? Math.round(raw / 15) * 15
    : Math.abs(raw - right) <= RIGHT_ANGLE_SNAP_DEG
      ? right
      : raw;
  return { ...original, rotationDeg: normalizeDeg(deg) };
};

/** 좌우 변을 끌어 줄바꿈 폭을 바꾼다. 반대쪽 변을 고정한다. 높이는 호출자가 textHeightMm 로 다시 계산한다. */
export const dragTextWidth = (
  original: TextNode,
  side: 'side-l' | 'side-r',
  current: PointMm,
): TextNode => {
  const local = componentToUnrotated(original, current);
  const width = Math.max(MIN_NODE_MM, side === 'side-r' ? local.x : original.width - local.x);
  // 고정할 변의 중점이 제자리에 있도록 새 중심을 회전축 방향으로 옮긴다(좌변 고정이면 +, 우변 고정이면 -).
  const shift = side === 'side-r' ? width - original.width : original.width - width;
  const rad = (original.rotationDeg * Math.PI) / 180;
  const c = center(original);
  const newCenterX = c.xMm + (shift / 2) * Math.cos(rad);
  const newCenterY = c.yMm + (shift / 2) * Math.sin(rad);
  return {
    ...original,
    width,
    x: newCenterX - width / 2,
    y: newCenterY - original.height / 2,
  };
};
